(function () {
  'use strict';

  var INSTANCE_PROP = '__axiomLenisController';
  var RAF_PROP = '__axiomLenisRaf';
  var lastResizeWidth = window.innerWidth;
  var lastResizeHeight = window.innerHeight;
  var scrollLockObserver = null;
  var vendorLoadPromise = null;
  var reducedMotionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;
  var primaryTouchQuery = window.matchMedia
    ? window.matchMedia('(hover: none) and (pointer: coarse)')
    : null;

  function prefersReducedMotion() {
    return Boolean(reducedMotionQuery && reducedMotionQuery.matches);
  }

  function usesNativeTouchScrolling() {
    return Boolean(primaryTouchQuery && primaryTouchQuery.matches);
  }

  function canUseLenis() {
    return isEnabled() && !prefersReducedMotion() && !usesNativeTouchScrolling();
  }

  function loadLenisVendor() {
    if (typeof window.Lenis === 'function') return Promise.resolve();
    if (vendorLoadPromise) return vendorLoadPromise;

    var source = document.body && document.body.dataset.lenisVendorScript;
    if (!source) return Promise.resolve();

    vendorLoadPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = source;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return vendorLoadPromise;
  }

  function isEnabled() {
    return document.body && document.body.getAttribute('data-lenis-enabled') === 'true';
  }

  function dispatchThemeResize() {
    document.dispatchEvent(new CustomEvent('theme:resize', {
      detail: {
        width: window.innerWidth,
        height: window.innerHeight,
        source: 'lenis'
      }
    }));
  }

  function isPageScrollLocked() {
    return document.documentElement.classList.contains('is-scroll-locked')
      || (document.body && document.body.style.overflow === 'hidden');
  }

  function isNativeScrollableOverlay(node) {
    return Boolean(
      node
      && node.nodeType === 1
      && node.matches
      && node.matches('top-level-modal')
    );
  }

  function syncPageScrollLock(controller) {
    if (!controller || !controller.lenis) return;

    if (isPageScrollLocked()) {
      controller.lenis.stop();
      return;
    }

    controller.lenis.start();
  }

  function observePageScrollLock(controller) {
    if (!document.body || typeof MutationObserver !== 'function') return;

    if (scrollLockObserver) scrollLockObserver.disconnect();
    scrollLockObserver = new MutationObserver(function () {
      syncPageScrollLock(controller);
    });
    scrollLockObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    scrollLockObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['style']
    });
    syncPageScrollLock(controller);
  }

  function resolveNativeScrollTop(target, offset) {
    if (typeof target === 'number') return target + offset;

    var element = target;
    if (typeof target === 'string') {
      if (target === 'top' || target === 'start' || target === '#') return offset;
      if (target === 'bottom' || target === 'end') {
        return document.documentElement.scrollHeight - window.innerHeight + offset;
      }
      element = document.querySelector(target);
    }

    if (!element || element.nodeType !== 1) return null;
    return element.getBoundingClientRect().top + window.scrollY + offset;
  }

  function installPublicApi() {
    window.Axiom = window.Axiom || {};
    window.Axiom.scrollTo = function (target, options) {
      var settings = options || {};
      var controller = window[INSTANCE_PROP];

      if (controller && controller.lenis) {
        controller.lenis.scrollTo(target, {
          immediate: Boolean(settings.immediate),
          offset: settings.offset || 0
        });
        return;
      }

      var scrollTop = resolveNativeScrollTop(target, settings.offset || 0);
      if (scrollTop === null) return;
      window.scrollTo({
        top: scrollTop,
        behavior: settings.immediate || prefersReducedMotion() ? 'auto' : 'smooth'
      });
    };
    window.Axiom.stopSmoothScroll = function () {
      var controller = window[INSTANCE_PROP];
      if (controller && controller.lenis) controller.lenis.stop();
    };
    window.Axiom.startSmoothScroll = function () {
      var controller = window[INSTANCE_PROP];
      if (controller && controller.lenis && !isPageScrollLocked()) controller.lenis.start();
    };
  }

  function createLenis() {
    if (
      !isEnabled()
      || prefersReducedMotion()
      || usesNativeTouchScrolling()
      || typeof window.Lenis !== 'function'
    ) return null;
    if (window[INSTANCE_PROP]) return window[INSTANCE_PROP];

    var lenis = new window.Lenis({
      lerp: 0.1,
      smoothWheel: true,
      syncTouch: false,
      prevent: isNativeScrollableOverlay
    });

    var controller = {
      lenis: lenis,
      rafId: 0,
      destroy: function () {
        if (this.rafId) window.cancelAnimationFrame(this.rafId);
        this.rafId = 0;
        if (scrollLockObserver) {
          scrollLockObserver.disconnect();
          scrollLockObserver = null;
        }
        lenis.destroy();
        if (window.lenis === lenis) window.lenis = null;
        if (window.Axiom && window.Axiom.lenis === lenis) window.Axiom.lenis = null;
        window[INSTANCE_PROP] = null;
        window[RAF_PROP] = null;
      }
    };

    function raf(time) {
      lenis.raf(time);
      controller.rafId = window.requestAnimationFrame(raf);
      window[RAF_PROP] = controller.rafId;
    }

    controller.rafId = window.requestAnimationFrame(raf);
    window[RAF_PROP] = controller.rafId;
    window[INSTANCE_PROP] = controller;
    window.lenis = lenis;
    window.Axiom = window.Axiom || {};
    window.Axiom.lenis = lenis;

    observePageScrollLock(controller);
    return controller;
  }

  function syncLenisEligibility() {
    var controller = window[INSTANCE_PROP];
    var shouldRun = canUseLenis();

    if (!shouldRun) {
      if (controller) controller.destroy();
      return;
    }

    if (controller) return;
    if (typeof window.Lenis === 'function') {
      createLenis();
      return;
    }

    loadLenisVendor().then(function () {
      if (canUseLenis()) createLenis();
    }).catch(function () {
      vendorLoadPromise = null;
    });
  }

  function handleResize() {
    if (window.innerWidth === lastResizeWidth && window.innerHeight === lastResizeHeight) return;
    lastResizeWidth = window.innerWidth;
    lastResizeHeight = window.innerHeight;

    var controller = window[INSTANCE_PROP];
    if (controller && controller.lenis && typeof controller.lenis.resize === 'function') {
      controller.lenis.resize();
    }

    dispatchThemeResize();
  }

  function initLenis() {
    installPublicApi();
    syncLenisEligibility();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLenis, { once: true });
  } else {
    initLenis();
  }

  window.addEventListener('resize', handleResize, { passive: true });
  if (reducedMotionQuery) reducedMotionQuery.addEventListener('change', syncLenisEligibility);
  if (primaryTouchQuery) primaryTouchQuery.addEventListener('change', syncLenisEligibility);
  window.addEventListener('pageshow', function () {
    syncLenisEligibility();
    var controller = window[INSTANCE_PROP];
    if (controller && controller.lenis && typeof controller.lenis.resize === 'function') {
      controller.lenis.resize();
    }
    syncPageScrollLock(controller);
  });

  document.addEventListener('shopify:section:load', function () {
    var controller = window[INSTANCE_PROP];
    if (controller && controller.lenis && typeof controller.lenis.resize === 'function') {
      window.requestAnimationFrame(function () {
        controller.lenis.resize();
        dispatchThemeResize();
      });
    }
  });
})();
